import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';

const LoginStripe = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') {
      setSessionExpiredMessage('Your session has expired. Please log in again.');
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSessionExpiredMessage('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      const redirect = sessionStorage.getItem('redirectAfterLogin');
      if (redirect) { sessionStorage.removeItem('redirectAfterLogin'); navigate(redirect); }
      else navigate('/dashboard');
    } else setError(result.error);
  };

  return (
    <PublicLayout>
      <SEO title="Client Login" description="Sign in to access your PlanGen Consulting project dashboard and waste management planning tools." canonicalPath="/login" noIndex />

      <section className="py-20 lg:py-28 bg-[#F8FAFC]" data-testid="login-section">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-[0_8px_60px_rgb(0,0,0,0.08)] border border-white/60 p-8" data-testid="login-card">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-[#0A2540]">Client Login</h1>
              <p className="text-slate-400 text-sm mt-1">Sign in to access your projects</p>
            </div>

            {sessionExpiredMessage && (
              <div className="bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-xl text-xs mb-4">{sessionExpiredMessage}</div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-xl text-xs mb-4">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all placeholder:text-slate-300"
                  placeholder="you@example.com" data-testid="login-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Password</label>
                <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all placeholder:text-slate-300"
                  placeholder="Enter your password" data-testid="login-password" />
                <div className="mt-1 text-right">
                  <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700">Forgot password?</Link>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-2.5 rounded-full bg-[#0A2540] text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-50 shadow-lg shadow-slate-900/10"
                data-testid="login-submit">
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            <p className="text-center text-xs text-slate-400 mt-5">
              Don't have an account? <Link to="/register" className="text-blue-600 font-medium hover:text-blue-700">Create one</Link>
            </p>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            <Link to="/" className="hover:text-slate-600 transition-colors">&larr; Back to homepage</Link>
          </p>
        </div>
      </section>
    </PublicLayout>
  );
};

export default LoginStripe;
